import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  authLogin,
  authLogout,
  authMe,
  authRegister,
  deleteCloudTicket,
  listCloudTickets,
  saveCloudTicket,
} from '../api/bookingApi';
import {
  loadLocalTickets,
  mergeTickets,
  removeLocalTicket,
  saveLocalTickets,
  upsertLocalTicket,
} from '../booking/ticketStore';

const AUTH_TOKEN_KEY = 'superfeo_auth_token_v1';
const AUTH_USER_KEY = 'superfeo_auth_user_v1';

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(
    () => localStorage.getItem(AUTH_TOKEN_KEY) || ''
  );
  const [user, setUser] = useState(readStoredUser);
  const [tickets, setTickets] = useState(() => loadLocalTickets());
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  const persistSession = useCallback((nextToken, nextUser) => {
    setToken(nextToken || '');
    setUser(nextUser || null);
    if (nextToken) localStorage.setItem(AUTH_TOKEN_KEY, nextToken);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
    if (nextUser) localStorage.setItem(AUTH_USER_KEY, JSON.stringify(nextUser));
    else localStorage.removeItem(AUTH_USER_KEY);
  }, []);

  const refreshLocal = useCallback(() => {
    setTickets(loadLocalTickets());
  }, []);

  const syncFromServer = useCallback(
    async (authToken) => {
      if (!authToken) {
        setTickets(loadLocalTickets());
        return;
      }
      try {
        const remote = await listCloudTickets(authToken);
        const local = loadLocalTickets();
        const merged = mergeTickets(local, remote);
        saveLocalTickets(merged);
        setTickets(merged);
        // Upload locals missing on server (best-effort)
        const remoteKeys = new Set(
          remote.map((b) => b.reference || b.tripId).filter(Boolean)
        );
        for (const b of local) {
          const key = b.reference || b.tripId;
          if (key && !remoteKeys.has(key)) {
            try {
              await saveCloudTicket(authToken, b);
            } catch {
              /* ignore single failures */
            }
          }
        }
      } catch {
        setTickets(loadLocalTickets());
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (token) {
        try {
          const me = await authMe(token);
          if (!cancelled) persistSession(token, me.user);
          if (!cancelled) await syncFromServer(token);
        } catch {
          if (!cancelled) {
            persistSession('', null);
            setTickets(loadLocalTickets());
          }
        }
      } else {
        setTickets(loadLocalTickets());
      }
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, persistSession, syncFromServer]);

  const register = useCallback(
    async ({ phone, password, name, email, ageGroup }) => {
      setBusy(true);
      try {
        const data = await authRegister({ phone, password, name, email, ageGroup });
        persistSession(data.token, data.user);
        await syncFromServer(data.token);
        return data;
      } finally {
        setBusy(false);
      }
    },
    [persistSession, syncFromServer]
  );

  const login = useCallback(
    async ({ phone, password, email, identifier }) => {
      setBusy(true);
      try {
        const data = await authLogin({ phone, password, email, identifier });
        persistSession(data.token, data.user);
        await syncFromServer(data.token);
        return data.user;
      } finally {
        setBusy(false);
      }
    },
    [persistSession, syncFromServer]
  );

  const logout = useCallback(async () => {
    try {
      if (token) await authLogout(token);
    } catch {
      /* ignore */
    }
    persistSession('', null);
    setTickets(loadLocalTickets());
  }, [token, persistSession]);

  const rememberTicket = useCallback(
    async (booking) => {
      const next = upsertLocalTicket(booking);
      setTickets(next);
      if (token && booking) {
        try {
          await saveCloudTicket(token, booking);
        } catch {
          /* local is enough */
        }
      }
      return next;
    },
    [token]
  );

  const forgetTicket = useCallback(
    async (reference) => {
      const next = removeLocalTicket(reference);
      setTickets(next);
      if (token && reference) {
        try {
          await deleteCloudTicket(token, reference);
        } catch {
          /* ignore */
        }
      }
      return next;
    },
    [token]
  );

  const value = useMemo(
    () => ({
      ready,
      busy,
      token,
      user,
      isLoggedIn: Boolean(token && user),
      tickets,
      register,
      login,
      logout,
      rememberTicket,
      forgetTicket,
      refreshLocal,
      syncFromServer: () => syncFromServer(token),
    }),
    [
      ready,
      busy,
      token,
      user,
      tickets,
      register,
      login,
      logout,
      rememberTicket,
      forgetTicket,
      refreshLocal,
      syncFromServer,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
